import { useEffect, useState, useMemo, useRef } from "react";
import Header from "../components/Header";
import { useData } from "../context/DataContext";
import { useCacheCheck } from "../hooks/useCacheCheck";
import { CacheStatus } from "../components/CacheStatus";
import { BuildingOffice2Icon, UserGroupIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/20/solid';
import React from 'react';
import { formatCurrency } from '../utils/formatters';
import { ExportButton } from '../components/ExportButton';
import SingleSelectDropdown from '../components/SingleSelectDropdown';
import { supabase } from '../lib/supabase';

type FilterType = 'hotel' | 'client';

function MultiSelectDropdown<T extends string | number>({
  label,
  options,
  selected,
  onChange,
  renderOption,
  enableSearch,
  disabledOptions,
  onSearch,
  onScrollToEnd,
  loading,
  summary,
}: {
  label: string;
  options: T[];
  selected: Set<T>;
  onChange: (newSelected: Set<T>) => void;
  renderOption?: (value: T) => string;
  enableSearch?: boolean;
  disabledOptions?: Set<T>;
  onSearch?: (search: string) => void;
  onScrollToEnd?: () => void;
  loading?: boolean;
  summary: string;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  const toggleOption = (value: T) => {
    const newSelected = new Set(selected);
    newSelected.has(value) ? newSelected.delete(value) : newSelected.add(value);
    onChange(newSelected);
  };

  const toggleAll = () => {
    onChange(enabledSelectedCount === enabledOptions.length ? new Set() : new Set(enabledOptions));
  };

  // Compute enabled options
  const enabledOptions = disabledOptions
    ? options.filter(opt => !disabledOptions.has(opt))
    : options;
  const enabledSelectedCount = enabledOptions.filter(opt => selected.has(opt)).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Filter options if search is enabled and search is not empty
  const filteredOptions = enableSearch && search
    ? options.filter(opt => (renderOption ? renderOption(opt) : String(opt)).toLowerCase().includes(search.toLowerCase()))
    : options;

  // Remove disabled options from selected when disabledOptions changes
  useEffect(() => {
    if (!disabledOptions) return;
    const prunedSelected = new Set([...selected].filter(opt => !disabledOptions.has(opt)));
    if (prunedSelected.size !== selected.size) {
      onChange(prunedSelected);
    }
  }, [disabledOptions]);

  return (
    <div className="relative inline-block w-52 text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="btn-secondary w-full !justify-between text-left font-medium"
      >
        <span className="min-w-0 truncate">{summary}</span>
        {open ? (
          <ChevronUpIcon className="h-4 w-4 shrink-0 text-slate-500" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-500" />
        )}
      </button>

      {open && (
        <div className="absolute z-10 mt-2 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200/90 bg-white shadow-lg ring-1 ring-slate-900/5">
          {enableSearch && (
            <div className="p-2">
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-800 focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  onSearch?.(e.target.value);
                }}
              />
            </div>
          )}
          <ul
            className="py-1 text-sm text-slate-700"
            onScroll={e => {
              const target = e.target as HTMLUListElement;
              if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
                onScrollToEnd?.();
              }
            }}
          >
            <li
              className="flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-slate-50"
              onClick={toggleAll}
            >
              <span>Select All {label}</span>
              {enabledSelectedCount === enabledOptions.length && enabledOptions.length > 0 && (
                <CheckIcon className="h-4 w-4 shrink-0 text-action" />
              )}
            </li>
            <hr />
            {filteredOptions.map((opt) => {
              const isDisabled = disabledOptions?.has(opt);
              return (
                <li
                  key={opt}
                  className={`flex items-center justify-between px-3 py-2 ${isDisabled ? 'cursor-not-allowed bg-slate-50 text-slate-400' : 'cursor-pointer hover:bg-slate-50'}`}
                  onClick={isDisabled ? undefined : () => toggleOption(opt)}
                  aria-disabled={isDisabled}
                >
                  <span>{renderOption ? renderOption(opt) : opt}</span>
                  {selected.has(opt) && !isDisabled && (
                    <CheckIcon className="h-4 w-4 shrink-0 text-action" />
                  )}
                </li>
              );
            })}
            {loading && (
              <li className="px-3 py-2 text-center text-slate-400">Loading...</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// Helper for summary label (generic)
function getSummary<T>(label: string, selected: Set<T>, options: T[], disabledOptions?: Set<T>, forceCount?: boolean) {
  const enabledOptions = disabledOptions
    ? options.filter(opt => !disabledOptions.has(opt))
    : options;
  const enabledSelectedCount = enabledOptions.filter(opt => selected.has(opt)).length;
  const anyDisabled = disabledOptions && disabledOptions.size > 0;
  
  // If no items are selected, show total available count
  if (enabledSelectedCount === 0) {
    return `0 / ${enabledOptions.length} ${label}`;
  }
  
  if (!anyDisabled && enabledSelectedCount === enabledOptions.length && enabledOptions.length > 0 && !forceCount) {
    return `All ${label} (${enabledOptions.length})`;
  }
  return `${enabledSelectedCount} / ${enabledOptions.length} ${label}`;
}

export default function MeetingPreparation() {
  // Basic state
  const currentYear = new Date().getFullYear();
  const [yearX, setYearX] = useState<number>(currentYear);
  const [yearY, setYearY] = useState<number>(currentYear - 1);
  const [filterType, setFilterType] = useState<FilterType>('hotel');
  const [isValidated, setIsValidated] = useState(false);
  
  // Filter state
  const [selectedChain, setSelectedChain] = useState<string>('');
  const [selectedHotels, setSelectedHotels] = useState<Set<string>>(new Set());
  const [selectedClient, setSelectedClient] = useState<string>('');
  
  // Expandable state for Top 5 tables
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set());
  
  // Available options
  const [availableChains, setAvailableChains] = useState<string[]>([]);
  const [availableHotels, setAvailableHotels] = useState<string[]>([]);
  const [availableClients, setAvailableClients] = useState<string[]>([]);
  const [availableArrivalYears, setAvailableArrivalYears] = useState<number[]>([]);
  const [catalogHotelsForChain, setCatalogHotelsForChain] = useState<string[]>([]);

  const { data, loadingProgress } = useData();
  
  // Check cache when component mounts
  useCacheCheck();

  // Initialize available options when data loads
  useEffect(() => {
    if (!data || data.length === 0) return;

    // Get available years
    const arrivalYears = Array.from(new Set(data.map(row => row.arrival_year).filter(Boolean))).sort((a, b) => b - a);
    setAvailableArrivalYears(arrivalYears);
    
    // Set initial years
    if (arrivalYears.length > 0) {
      const mostRecentYear = arrivalYears[0];
      if (arrivalYears.includes(currentYear)) {
        setYearX(currentYear);
        const previousYear = currentYear - 1;
        if (arrivalYears.includes(previousYear)) {
          setYearY(previousYear);
        } else {
          setYearY(mostRecentYear);
        }
      } else {
        setYearX(mostRecentYear);
        if (arrivalYears.length > 1) {
          setYearY(arrivalYears[1]);
        } else {
          setYearY(mostRecentYear);
        }
      }
    }
    
    // Get available chains (hotel groups)
    const chains = Array.from(new Set(data.map(row => row.hotel_group_name).filter(Boolean))).sort();
    setAvailableChains(chains);

    // Get available hotels
    const hotels = Array.from(new Set(data.map(row => row.venue).filter(Boolean))).sort();
    setAvailableHotels(hotels);

    // Get available clients
    const clients = Array.from(new Set(data.map(row => row.company).filter(Boolean))).sort();
    setAvailableClients(clients);
  }, [data, currentYear]);

  // Store validated filter state
  const [validatedFilters, setValidatedFilters] = useState<{
    filterType: FilterType;
    selectedChain: string;
    selectedHotels: Set<string>;
    selectedClient: string;
    yearX: number;
    yearY: number;
  } | null>(null);

  // Check if user has made any valid selection
  const hasValidSelection = useMemo(() => {
    if (filterType === 'hotel') {
      return selectedChain !== '' || selectedHotels.size > 0;
    } else {
      return selectedClient !== '';
    }
  }, [filterType, selectedChain, selectedHotels.size, selectedClient]);

  // Check if current selection differs from validated selection
  const hasChangedSinceValidation = useMemo(() => {
    if (!validatedFilters) return hasValidSelection;
    
    return (
      validatedFilters.filterType !== filterType ||
      validatedFilters.selectedChain !== selectedChain ||
      validatedFilters.selectedClient !== selectedClient ||
      validatedFilters.yearX !== yearX ||
      validatedFilters.yearY !== yearY ||
      validatedFilters.selectedHotels.size !== selectedHotels.size ||
      !Array.from(validatedFilters.selectedHotels).every(hotel => selectedHotels.has(hotel)) ||
      !Array.from(selectedHotels).every(hotel => validatedFilters.selectedHotels.has(hotel))
    );
  }, [validatedFilters, filterType, selectedChain, selectedClient, yearX, yearY, selectedHotels]);

  // Determine if validate button should be enabled
  const isValidateEnabled = hasValidSelection && hasChangedSinceValidation;

  // Handle validation
  const handleValidate = () => {
    setValidatedFilters({
      filterType,
      selectedChain,
      selectedHotels: new Set(selectedHotels),
      selectedClient,
      yearX,
      yearY
    });
    setIsValidated(true);
  };

  // Handle reset
  const handleReset = () => {
    setSelectedChain('');
    setSelectedHotels(new Set());
    setSelectedClient('');
    setIsValidated(false);
    setValidatedFilters(null);
    setExpandedCountries(new Set());
  };

  // Fetch all hotels from catalog when a chain is selected (includes hotels with no report data).
  useEffect(() => {
    if (!selectedChain) {
      setCatalogHotelsForChain([]);
      return;
    }

    let cancelled = false;

    (async () => {
      const { data: rows, error } = await supabase
        .from('hotels')
        .select('title')
        .eq('hotel_group_name', selectedChain)
        .order('title');

      if (cancelled) return;

      if (error) {
        console.error('MeetingPreparation: catalog hotels fetch', error);
        setCatalogHotelsForChain([]);
        return;
      }

      setCatalogHotelsForChain(
        (rows || []).map((r) => r.title).filter(Boolean),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedChain]);

  // Filter hotels based on selected chain (report data + catalog)
  const filteredHotels = useMemo(() => {
    if (!selectedChain) {
      return availableHotels;
    }

    const hotelsInSelectedChain = new Set<string>(
      data
        ?.filter((row) => row.hotel_group_name === selectedChain)
        .map((row) => row.venue)
        .filter(Boolean) ?? [],
    );

    catalogHotelsForChain.forEach((title) => hotelsInSelectedChain.add(title));

    return Array.from(hotelsInSelectedChain).sort();
  }, [data, selectedChain, availableHotels, catalogHotelsForChain]);

  // Auto-select all hotels when a chain is selected, or clear when chain is cleared
  useEffect(() => {
    if (selectedChain) {
      // When a chain is selected, automatically select all hotels from that chain
      setSelectedHotels(new Set(filteredHotels));
    } else {
      // When no chain is selected, clear hotel selections
      setSelectedHotels(new Set());
    }
  }, [selectedChain, filteredHotels]);

  // Calculate KPIs based on validated filters
  const kpiData = useMemo(() => {
    if (!data || !isValidated || !validatedFilters) return [];

    // Filter data based on validated criteria
    let filteredData = data.filter(row => {
      // Filter by arrival year
      if (row.arrival_year !== validatedFilters.yearX && row.arrival_year !== validatedFilters.yearY) return false;
      
      // Filter by hotel or client selection
      if (validatedFilters.filterType === 'hotel') {
        if (validatedFilters.selectedChain && row.hotel_group_name !== validatedFilters.selectedChain) return false;
        if (validatedFilters.selectedHotels.size > 0 && !validatedFilters.selectedHotels.has(row.venue)) return false;
    } else {
        if (validatedFilters.selectedClient && row.company !== validatedFilters.selectedClient) return false;
      }

      return true;
    });

    console.log('Filtered data count:', filteredData.length);
    console.log('Sample filtered data:', filteredData.slice(0, 3));
    console.log('Unique segments in filtered data:', [...new Set(filteredData.map(row => row.segment))]);
    console.log('Validated filters:', validatedFilters);

    // Group by segment and calculate KPIs for each year
    const segments = ['Ad-hoc', 'Series', 'AC/RC', 'FIT'];
    
    return segments.map(segment => {
      const segmentData = filteredData.filter(row => {
        // Map segment names to match database values
        if (segment === 'Ad-hoc' && (row.segment === 'Ad-hoc Group' || row.segment === 'ASIA  - Ad-hoc Group' || row.segment === 'Ad-hoc' || !row.segment)) return true;
        if (segment === 'Series' && (row.segment === 'Group Series' || row.segment === 'ASIA - Group Series' || row.segment === 'Series')) return true;
        if (segment === 'AC/RC' && (row.segment === 'Allotment contract' || row.segment === 'ASIA - Allotment Contract' || row.segment === 'Rate Contract' || row.segment === 'ASIA - Rate Contract' || row.segment === 'AC' || row.segment === 'RC')) return true;
        if (segment === 'FIT' && row.segment === 'FIT') return true;
        return false;
      });

      // Calculate KPIs for Year X
      const dataX = segmentData.filter(row => row.arrival_year === validatedFilters.yearX);
      const totalGroupsX = dataX.length;
      const confirmedGroupsX = dataX.filter(row => row.status === 'Booked').length;
      const totalExpectedRevenueX = dataX.filter(row => row.status === 'Booked').reduce((sum, row) => sum + (row.estimated_revenue || 0), 0);
      const totalConfirmedRevenueX = dataX.filter(row => row.status === 'Booked').reduce((sum, row) => sum + (row.realised_revenue || 0), 0);
      const materializationRateX = totalExpectedRevenueX > 0 ? (totalConfirmedRevenueX / totalExpectedRevenueX) * 100 : null;

      // Calculate KPIs for Year Y
      const dataY = segmentData.filter(row => row.arrival_year === validatedFilters.yearY);
      const totalGroupsY = dataY.length;
      const confirmedGroupsY = dataY.filter(row => row.status === 'Booked').length;
      const totalExpectedRevenueY = dataY.filter(row => row.status === 'Booked').reduce((sum, row) => sum + (row.estimated_revenue || 0), 0);
      const totalConfirmedRevenueY = dataY.filter(row => row.status === 'Booked').reduce((sum, row) => sum + (row.realised_revenue || 0), 0);
      const materializationRateY = totalExpectedRevenueY > 0 ? (totalConfirmedRevenueY / totalExpectedRevenueY) * 100 : null;

    return {
        segment,
        totalGroupsX,
        totalGroupsY,
        confirmedGroupsX,
        confirmedGroupsY,
        totalExpectedRevenueX,
        totalExpectedRevenueY,
        totalConfirmedRevenueX,
        totalConfirmedRevenueY,
        materializationRateX,
        materializationRateY
      };
    });
  }, [data, isValidated, validatedFilters]);


  // Calculate totals for the bottom row
  const totals = useMemo(() => {
    return kpiData.reduce((acc, row) => {
      acc.totalGroupsX += row.totalGroupsX;
      acc.totalGroupsY += row.totalGroupsY;
      acc.confirmedGroupsX += row.confirmedGroupsX;
      acc.confirmedGroupsY += row.confirmedGroupsY;
      acc.totalExpectedRevenueX += row.totalExpectedRevenueX;
      acc.totalExpectedRevenueY += row.totalExpectedRevenueY;
      acc.totalConfirmedRevenueX += row.totalConfirmedRevenueX;
      acc.totalConfirmedRevenueY += row.totalConfirmedRevenueY;
      return acc;
    }, {
      totalGroupsX: 0,
      totalGroupsY: 0,
      confirmedGroupsX: 0,
      confirmedGroupsY: 0,
      totalExpectedRevenueX: 0,
      totalExpectedRevenueY: 0,
      totalConfirmedRevenueX: 0,
      totalConfirmedRevenueY: 0,
      materializationRateX: null as number | null,
      materializationRateY: null as number | null
    });
  }, [kpiData]);

  // Calculate total materialization rates
  totals.materializationRateX = totals.totalExpectedRevenueX > 0 ? (totals.totalConfirmedRevenueX / totals.totalExpectedRevenueX) * 100 : null;
  totals.materializationRateY = totals.totalExpectedRevenueY > 0 ? (totals.totalConfirmedRevenueY / totals.totalExpectedRevenueY) * 100 : null;

  // Table data transformer for export
  const getTableDataForExport = () => {
    if (!isValidated || !validatedFilters) return [];

    const exportData: any[] = [];

    // Add each segment's data
    kpiData.forEach(row => {
      // Segment header
      exportData.push({
        'Facts & Figures': row.segment,
        [`Arrival Year ${validatedFilters.yearX}`]: '',
        [`Arrival Year ${validatedFilters.yearY}`]: ''
      });

      // Total groups
      exportData.push({
        'Facts & Figures': 'Total groups',
        [`Arrival Year ${validatedFilters.yearX}`]: row.totalGroupsX,
        [`Arrival Year ${validatedFilters.yearY}`]: row.totalGroupsY
      });

      // Confirmed groups
      exportData.push({
        'Facts & Figures': 'Confirmed groups',
        [`Arrival Year ${validatedFilters.yearX}`]: row.confirmedGroupsX,
        [`Arrival Year ${validatedFilters.yearY}`]: row.confirmedGroupsY
      });

      // Total expected revenue
      exportData.push({
        'Facts & Figures': 'Total expected revenue',
        [`Arrival Year ${validatedFilters.yearX}`]: row.totalExpectedRevenueX,
        [`Arrival Year ${validatedFilters.yearY}`]: row.totalExpectedRevenueY
      });

      // Total confirmed revenue
      exportData.push({
        'Facts & Figures': 'Total confirmed revenue',
        [`Arrival Year ${validatedFilters.yearX}`]: row.totalConfirmedRevenueX,
        [`Arrival Year ${validatedFilters.yearY}`]: row.totalConfirmedRevenueY
      });

      // Materialization ratio
      exportData.push({
        'Facts & Figures': 'Materialization ratio',
        [`Arrival Year ${validatedFilters.yearX}`]: row.materializationRateX !== null ? row.materializationRateX : 0,
        [`Arrival Year ${validatedFilters.yearY}`]: row.materializationRateY !== null ? row.materializationRateY : 0
      });
    });

    // Add Top 5 Hotels table (for hotel selection)
    if (validatedFilters.filterType === 'hotel' && top5HotelsData.length > 0) {
      // Add separator
      exportData.push({
        'Facts & Figures': '',
        [`Arrival Year ${validatedFilters.yearX}`]: '',
        [`Arrival Year ${validatedFilters.yearY}`]: ''
      });

      // Add Top 5 Hotels header
      exportData.push({
        'Facts & Figures': `Top 5 Hotels - ${validatedFilters.yearX}`,
        [`Arrival Year ${validatedFilters.yearX}`]: '',
        [`Arrival Year ${validatedFilters.yearY}`]: ''
      });

      // Add column headers
      exportData.push({
        'Facts & Figures': 'Hotels',
        [`Arrival Year ${validatedFilters.yearX}`]: 'Confirmed Groups',
        [`Arrival Year ${validatedFilters.yearY}`]: 'Confirmed Revenue'
      });

      // Add hotel data
      top5HotelsData.forEach(hotelData => {
        exportData.push({
          'Facts & Figures': hotelData.hotel,
          [`Arrival Year ${validatedFilters.yearX}`]: hotelData.groups,
          [`Arrival Year ${validatedFilters.yearY}`]: hotelData.revenue
        });
      });
    }

    // Add Top 5 Destinations table (for client selection)
    if (validatedFilters.filterType === 'client' && top5DestinationsData.length > 0) {
      // Add separator
      exportData.push({
        'Facts & Figures': '',
        [`Arrival Year ${validatedFilters.yearX}`]: '',
        [`Arrival Year ${validatedFilters.yearY}`]: ''
      });

      // Add Top 5 Destinations header
      exportData.push({
        'Facts & Figures': `Top 5 Destinations - ${validatedFilters.yearX}`,
        [`Arrival Year ${validatedFilters.yearX}`]: '',
        [`Arrival Year ${validatedFilters.yearY}`]: ''
      });

      // Add column headers
      exportData.push({
        'Facts & Figures': 'Destinations',
        [`Arrival Year ${validatedFilters.yearX}`]: 'Confirmed Groups',
        [`Arrival Year ${validatedFilters.yearY}`]: 'Confirmed Revenue'
      });

      // Add destination data with cities
      top5DestinationsData.forEach(countryData => {
        // Add country row
        exportData.push({
          'Facts & Figures': countryData.country,
          [`Arrival Year ${validatedFilters.yearX}`]: countryData.groups,
          [`Arrival Year ${validatedFilters.yearY}`]: countryData.revenue
        });

        // Add city breakdown
        countryData.cities.forEach(cityData => {
          exportData.push({
            'Facts & Figures': `  ${cityData.city}`, // Indent cities with spaces
            [`Arrival Year ${validatedFilters.yearX}`]: cityData.groups,
            [`Arrival Year ${validatedFilters.yearY}`]: cityData.revenue
          });
        });
      });
    }

    return exportData;
  };

  // Calculate Top 5 Destinations data (for client selection)
  const top5DestinationsData = useMemo(() => {
    if (!data || !isValidated || !validatedFilters || validatedFilters.filterType !== 'client') return [];

    // Filter data for client and booked status (Year X only)
    const filteredData = data.filter(row => {
      if (row.arrival_year !== validatedFilters.yearX) return false;
      if (validatedFilters.selectedClient && row.company !== validatedFilters.selectedClient) return false;
      if (row.status !== 'Booked') return false;
      return true;
    });

    // Group by country
    const countryData = new Map<string, { groups: number, revenue: number, cities: Map<string, { groups: number, revenue: number }> }>();
    
    filteredData.forEach(row => {
      const country = row.destination_country || 'Unknown';
      const city = row.destination_city || 'Unknown';
      
      if (!countryData.has(country)) {
        countryData.set(country, { groups: 0, revenue: 0, cities: new Map() });
      }
      
      const countryEntry = countryData.get(country)!;
      countryEntry.groups += 1;
      countryEntry.revenue += row.estimated_revenue || 0;
      
      if (!countryEntry.cities.has(city)) {
        countryEntry.cities.set(city, { groups: 0, revenue: 0 });
      }
      
      const cityEntry = countryEntry.cities.get(city)!;
      cityEntry.groups += 1;
      cityEntry.revenue += row.estimated_revenue || 0;
    });

    // Convert to array and sort by revenue, take top 5
    return Array.from(countryData.entries())
      .map(([country, data]) => ({
        country,
        groups: data.groups,
        revenue: data.revenue,
        cities: Array.from(data.cities.entries())
          .map(([city, cityData]) => ({ city, groups: cityData.groups, revenue: cityData.revenue }))
          .sort((a, b) => b.revenue - a.revenue)
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [data, isValidated, validatedFilters]);

  // Calculate Top 5 Hotels data (for hotel selection)
  const top5HotelsData = useMemo(() => {
    if (!data || !isValidated || !validatedFilters || validatedFilters.filterType !== 'hotel') return [];
    if (validatedFilters.selectedHotels.size < 2) return []; // Only show if at least 2 hotels selected

    // Filter data for hotels and booked status (Year X only)
    const filteredData = data.filter(row => {
      if (row.arrival_year !== validatedFilters.yearX) return false;
      if (validatedFilters.selectedChain && row.hotel_group_name !== validatedFilters.selectedChain) return false;
      if (validatedFilters.selectedHotels.size > 0 && !validatedFilters.selectedHotels.has(row.venue)) return false;
      if (row.status !== 'Booked') return false;
      return true;
    });

    // Group by hotel
    const hotelData = new Map<string, { groups: number, revenue: number }>();
    
    filteredData.forEach(row => {
      const hotel = row.venue || 'Unknown';
      
      if (!hotelData.has(hotel)) {
        hotelData.set(hotel, { groups: 0, revenue: 0 });
      }
      
      const hotelEntry = hotelData.get(hotel)!;
      hotelEntry.groups += 1;
      hotelEntry.revenue += row.estimated_revenue || 0;
    });

    // Convert to array and sort by revenue, take top 5
    return Array.from(hotelData.entries())
      .map(([hotel, data]) => ({ hotel, groups: data.groups, revenue: data.revenue }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [data, isValidated, validatedFilters]);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Meeting Preparation</h1>
          <div className="flex items-center space-x-4">
            {isValidated && (
              <ExportButton
                reportName="Meeting Preparation"
                data={data || []}
                filters={{
                  selectedCreationYears: new Set(),
                  selectedArrivalYears: validatedFilters ? new Set([validatedFilters.yearX, validatedFilters.yearY]) : new Set(),
                  selectedManagers: new Set(),
                  selectedSegments: new Set(),
                  selectedClients: validatedFilters?.selectedClient ? new Set([validatedFilters.selectedClient]) : new Set(),
                  selectedHotels: validatedFilters?.selectedHotels || new Set(),
                  selectedHotelGroups: validatedFilters?.selectedChain ? new Set([validatedFilters.selectedChain]) : new Set(),
                  selectedArrivalMonths: new Set(),
                  selectedArrivalQuarters: new Set(),
                  selectedCreationMonths: new Set(),
                  selectedCreationQuarters: new Set(),
                  allClientsSelected: !validatedFilters?.selectedClient,
                  allHotelsSelected: !validatedFilters?.selectedHotels || validatedFilters.selectedHotels.size === 0,
                  allHotelGroupsSelected: !validatedFilters?.selectedChain
                }}
                tableDataTransformer={getTableDataForExport}
              />
            )}
            <CacheStatus />
          </div>
        </div>

        {loadingProgress && (
          <div className="mb-4 bg-blue-50 text-blue-700 p-2 rounded-md text-center">
            {loadingProgress}
          </div>
        )}

        {/* Filter Controls */}
        <div className="mb-6 bg-gray-50 rounded-xl shadow-sm border">
          {/* Filter Type Buttons */}
          <div className="p-4 border-b">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilterType('hotel')}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-action/40 focus-visible:ring-offset-2 ${
                  filterType === 'hotel'
                    ? 'border border-action/25 bg-action text-white hover:bg-action-hover'
                    : 'border border-slate-300/90 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <BuildingOffice2Icon className="h-5 w-5 shrink-0" aria-hidden />
                Hotel
              </button>
              <button
                type="button"
                onClick={() => setFilterType('client')}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-action/40 focus-visible:ring-offset-2 ${
                  filterType === 'client'
                    ? 'border border-action/25 bg-action text-white hover:bg-action-hover'
                    : 'border border-slate-300/90 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <UserGroupIcon className="h-5 w-5 shrink-0" aria-hidden />
                Client
              </button>
            </div>
          </div>

          {/* Conditional Filter Dropdowns */}
          <div className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
            {filterType === 'hotel' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hotel Groups
                  </label>
                  <SingleSelectDropdown
                    label="Hotel Groups"
                    options={availableChains}
                    selected={selectedChain}
                    onChange={setSelectedChain}
                    enableSearch={true}
                    renderOption={opt => opt}
                    placeholder="Select a group"
                    className="w-52"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hotels
                  </label>
                  <MultiSelectDropdown
                    label="Hotels"
                    options={filteredHotels}
                    selected={selectedHotels}
                    onChange={setSelectedHotels}
                    enableSearch={true}
                    renderOption={opt => opt}
                    summary={
                      !selectedChain 
                        ? "Select a hotel" 
                        : selectedHotels.size === filteredHotels.length && filteredHotels.length > 0
                        ? `${filteredHotels.length} hotels`
                        : getSummary('Hotels', selectedHotels, filteredHotels)
                    }
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clients
                </label>
                <SingleSelectDropdown
                  label="Clients"
                  options={availableClients}
                  selected={selectedClient}
                  onChange={setSelectedClient}
                  enableSearch={true}
                  renderOption={opt => opt}
                  placeholder="Select a client"
                  className="w-52"
                />
              </div>
            )}

            {/* Year Selectors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arrival Year X
              </label>
              <select
                value={yearX}
                onChange={(e) => setYearX(Number(e.target.value))}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
              >
                {availableArrivalYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arrival Year Y
              </label>
              <select
                value={yearY}
                onChange={(e) => setYearY(Number(e.target.value))}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
              >
                {availableArrivalYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="ml-auto flex items-end gap-2">
              <button type="button" onClick={handleReset} className="btn-secondary !h-auto min-h-10 px-4 py-2">
                Reset
              </button>
              <button
                type="button"
                onClick={handleValidate}
                disabled={!isValidateEnabled}
                className="btn-primary min-h-10 px-6 py-2 disabled:cursor-not-allowed disabled:hover:bg-action"
              >
                Validate
              </button>
          </div>
        </div>
        </div>
        </div>

        {/* KPI Table */}
        {isValidated && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Facts & Figures
                    </th>
                    <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Arrival Year {validatedFilters?.yearX || yearX}
                    </th>
                    <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Arrival Year {validatedFilters?.yearY || yearY}
                    </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                  {kpiData.map((row) => (
                    <React.Fragment key={row.segment}>
                      {/* Segment Header */}
                      <tr className="bg-gray-50">
                        <td colSpan={3} className="px-3 py-1 text-sm font-semibold text-gray-900 text-left">
                          {row.segment}
                  </td>
                </tr>
                      
                      {/* Total Groups */}
                      <tr>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-gray-900">
                          Total groups
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {row.totalGroupsX}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {row.totalGroupsY}
                        </td>
                      </tr>
                      
                      {/* Confirmed Groups */}
                      <tr>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-gray-900">
                          Confirmed groups
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {row.confirmedGroupsX}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {row.confirmedGroupsY}
                        </td>
                      </tr>
                      
                      {/* Total Expected Revenue */}
                      <tr>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-gray-900">
                          Total expected revenue
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {formatCurrency(row.totalExpectedRevenueX)}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {formatCurrency(row.totalExpectedRevenueY)}
                        </td>
                      </tr>
                      
                      {/* Total Confirmed Revenue */}
                      <tr>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-gray-900">
                          Total confirmed revenue
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {formatCurrency(row.totalConfirmedRevenueX)}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {formatCurrency(row.totalConfirmedRevenueY)}
                        </td>
                      </tr>
                      
                      {/* Materialization Ratio */}
                      <tr>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-gray-900">
                          Materialization ratio
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {row.materializationRateX !== null ? `${row.materializationRateX.toFixed(1)}%` : 'N/A'}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900">
                          {row.materializationRateY !== null ? `${row.materializationRateY.toFixed(1)}%` : 'N/A'}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  
              </tbody>
            </table>
                    </div>
        </div>
        )}

        {/* Top 5 Destinations Table (Client Selection) */}
        {isValidated && validatedFilters?.filterType === 'client' && top5DestinationsData.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Top 5 Destinations - {validatedFilters?.yearX || yearX}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Header Row */}
                  <tr className="bg-gray-50">
                    <td className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Destinations
                    </td>
                    <td className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Confirmed Groups
                    </td>
                    <td className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Confirmed Revenue
                    </td>
                  </tr>
                  {top5DestinationsData.map((countryData) => (
                    <React.Fragment key={countryData.country}>
                      {/* Country Row */}
                      <tr 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setExpandedCountries(prev => {
                            const next = new Set(prev);
                            if (next.has(countryData.country)) {
                              next.delete(countryData.country);
                            } else {
                              next.add(countryData.country);
                            }
                            return next;
                          });
                        }}
                      >
                        <td className="px-3 py-1 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center w-1/3">
                          {expandedCountries.has(countryData.country) ? (
                            <ChevronUpIcon className="w-4 h-4 mr-2" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4 mr-2" />
                          )}
                          {countryData.country}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900 w-1/3">
                          {countryData.groups}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900 w-1/3">
                          {formatCurrency(countryData.revenue)}
                        </td>
                      </tr>
                      
                      {/* City Breakdown */}
                      {expandedCountries.has(countryData.country) && countryData.cities.map(cityData => (
                        <tr key={`${countryData.country}-${cityData.city}`} className="bg-gray-50">
                          <td className="px-8 py-1 whitespace-nowrap text-sm text-gray-700 w-1/3">
                            {cityData.city}
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-700 w-1/3">
                            {cityData.groups}
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-700 w-1/3">
                            {formatCurrency(cityData.revenue)}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Top 5 Hotels Table (Hotel Selection) */}
        {isValidated && validatedFilters?.filterType === 'hotel' && top5HotelsData.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden mt-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Top 5 Hotels - {validatedFilters?.yearX || yearX}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Header Row */}
                  <tr className="bg-gray-50">
                    <td className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Hotels
                    </td>
                    <td className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Confirmed Groups
                    </td>
                    <td className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                      Confirmed Revenue
                    </td>
                  </tr>
                  {top5HotelsData.map((hotelData) => (
                    <tr key={hotelData.hotel} className="hover:bg-gray-50">
                      <td className="px-3 py-1 whitespace-nowrap text-sm font-medium text-gray-900 w-1/3">
                        {hotelData.hotel}
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900 w-1/3">
                        {hotelData.groups}
                      </td>
                      <td className="px-3 py-1 whitespace-nowrap text-sm text-center text-gray-900 w-1/3">
                        {formatCurrency(hotelData.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}